import { isRouteAvailableInLocale } from "@/lib/i18n/route-equivalence";
/**
 * Services mega menu content — single source: `src/data/nav-services-mega.json`.
 *
 * JSON schema:
 * - `kicker`, `seeAll`, `viewService`: localized strings (`en`, `sv`, …).
 *   `viewService` may include `{name}`; replaced with the nav service title from `services.json`.
 * - `columns`: map of **service tag** → column definition (keys must match routable nav services).
 *   - `links[]`: placeholder sub-pages under that service.
 *     - `key`: stable sub-page identity; default URL segment after the service slug.
 *     - `slug` (optional): localized public URL segment.
 *     - `icon`: Lucide name for the services directory card (unique within the column).
 *     - `iconBrand` (optional): tool logo stem for integration sub-pages on directory cards.
 *     - `title`, `description`: localized copy shown in the mega panel / mobile drawer.
 *
 * Column headers, parent hrefs, and `{name}` in `viewService` come from `getNavServiceItems()` only.
 *
 * Service pages that list the same sub-pages (e.g. SEO hub) must use
 * `getNavServicesMegaColumns()` — do not duplicate `links` in TS. See `docs/content-sources.md`.
 */
import navServicesMegaData from "@/data/nav-services-mega.json";
import type { CcServiceDirectoryBrandIconId } from "@/lib/cc-service-directory-brand-icons";
import type { CcServiceDirectoryLinkIconName } from "@/lib/cc-service-directory-icons";
import { isServiceSubpagePublished } from "@/lib/cc-service-subpages-publish";
import { t } from "@/lib/content";
import { buildServiceSubpageHref } from "@/lib/i18n/paths";
import { toSiteLocale, type SiteLocale } from "@/lib/i18n/config";
import { getNavServiceItems } from "@/lib/service-nav-items";
import type { NavServiceItem } from "@/lib/services";
import type { Lang } from "@/lib/types";

/** Localized field object keyed by site language code. */
export type NavServicesMegaLocalizedString = Record<string, string>;

export type NavServicesMegaLinkDef = {
  key: string;
  /** Only locales with the same identity represent translated equivalents. */
  alternateIdentity?: Partial<Record<SiteLocale, string>>;
  /** Optional public URL segment per locale; key remains the stable content identity. */
  slug?: NavServicesMegaLocalizedString;
  standaloneLocales?: string[];
  /** Only these site locales have this sub-page (market-only subjects, e.g. Dinero and Billy in Danish). */
  locales?: SiteLocale[];
  icon: CcServiceDirectoryLinkIconName;
  /** One logo for all languages, or one per language (e.g. Xero in English, Fortnox in Swedish). */
  iconBrand?: CcServiceDirectoryBrandIconId | Partial<Record<Lang, CcServiceDirectoryBrandIconId>>;
  title: NavServicesMegaLocalizedString;
  description: NavServicesMegaLocalizedString;
};

export type NavServicesMegaColumnDef = {
  links: NavServicesMegaLinkDef[];
};

/** Shape of `src/data/nav-services-mega.json`. */
export type NavServicesMegaData = {
  kicker: NavServicesMegaLocalizedString;
  seeAll: NavServicesMegaLocalizedString;
  viewService: NavServicesMegaLocalizedString;
  columns: Record<string, NavServicesMegaColumnDef>;
};

// Market-limited links (`locales`) only carry labels for their own languages.
const megaData = navServicesMegaData as unknown as NavServicesMegaData;

export type NavServiceMegaLink = {
  key: string;
  icon: CcServiceDirectoryLinkIconName;
  iconBrand?: CcServiceDirectoryBrandIconId;
  label: string;
  description: string;
  href: string;
};

export type NavServiceMegaColumn = {
  tag: string;
  header: string;
  headerHref: string;
  viewLabel: string;
  links: NavServiceMegaLink[];
};

export type NavServicesMegaCopy = {
  kicker: string;
  seeAll: string;
};

export function getNavServicesMegaCopy(lang: Lang): NavServicesMegaCopy {
  return {
    kicker: t(megaData.kicker, lang),
    seeAll: t(megaData.seeAll, lang),
  };
}

export type NavServicesMegaColumnsOptions = {
  /** Include planned sub-links for internal lookups. Visible navigation omits this. */
  includeUnpublished?: boolean;
};

/** One column per routable nav service, with placeholder sub-page links. */
export function getNavServicesMegaColumns(
  lang: Lang,
  options?: NavServicesMegaColumnsOptions,
): NavServiceMegaColumn[] {
  const locale = toSiteLocale(lang);
  const serviceItems = getNavServiceItems(lang);

  return serviceItems.map((item) =>
    columnFromServiceItem(item, lang, locale, options),
  );
}

/** Single mega column for one service hub (e.g. filtered `CcServiceDirectory`). */
export function getNavServiceMegaColumn(
  lang: Lang,
  serviceTag: string,
  options?: NavServicesMegaColumnsOptions,
): NavServiceMegaColumn | undefined {
  return getNavServicesMegaColumns(lang, options).find(
    (column) => column.tag === serviceTag,
  );
}

export type NavServiceMegaFlatLink = {
  label: string;
  href: string;
};

/** Column parent + sub-page hrefs (tests / internal discovery helpers). */
export function flattenNavServiceMegaAllLinks(
  columns: NavServiceMegaColumn[],
): NavServiceMegaFlatLink[] {
  return columns.flatMap((column) => [
    { label: column.header, href: column.headerHref },
    ...column.links.map((link) => ({ label: link.label, href: link.href })),
  ]);
}

/** Flat list of mega sub-links (for active-state checks). */
export function flattenNavServiceMegaLinks(
  columns: NavServiceMegaColumn[],
): NavServiceMegaLink[] {
  return columns.flatMap((column) => column.links);
}

export type NavServiceMegaColumnExclude = {
  /** Sub-page segment (`nav-services-mega.json` link `key`). */
  excludeSubKey?: string;
  /** Full marketing path; excludes the card whose `href` matches after normalization. */
  excludeHref?: string;
};

export function normalizeMarketingPath(path: string): string {
  const withoutQuery = path.split("?")[0]?.split("#")[0] ?? path;
  const trimmed = withoutQuery.replace(/\/+$/, "");
  return trimmed || "/";
}

export function shouldExcludeNavServiceMegaLink(
  link: NavServiceMegaLink,
  exclude: NavServiceMegaColumnExclude,
): boolean {
  if (exclude.excludeSubKey && link.key === exclude.excludeSubKey) {
    return true;
  }
  if (exclude.excludeHref) {
    const href = normalizeMarketingPath(link.href);
    const target = normalizeMarketingPath(exclude.excludeHref);
    if (href === target) {
      return true;
    }
  }
  return false;
}

/** Drop excluded sub-page cards; omit columns with no links left. */
export function filterNavServiceMegaColumns(
  columns: NavServiceMegaColumn[],
  exclude?: NavServiceMegaColumnExclude,
): NavServiceMegaColumn[] {
  if (!exclude?.excludeSubKey && !exclude?.excludeHref) {
    return columns;
  }
  return columns
    .map((column) => ({
      ...column,
      links: column.links.filter(
        (link) => !shouldExcludeNavServiceMegaLink(link, exclude),
      ),
    }))
    .filter((column) => column.links.length > 0);
}

function resolveIconBrand(
  iconBrand: NavServicesMegaLinkDef["iconBrand"],
  lang: Lang,
): CcServiceDirectoryBrandIconId | undefined {
  if (!iconBrand || typeof iconBrand === "string") return iconBrand;
  return iconBrand[lang] ?? iconBrand.en;
}

function columnFromServiceItem(
  item: NavServiceItem,
  lang: Lang,
  locale: ReturnType<typeof toSiteLocale>,
  options?: NavServicesMegaColumnsOptions,
): NavServiceMegaColumn {
  const columnDef = megaData.columns[item.tag];
  const showUnpublished = options?.includeUnpublished === true;
  const links: NavServiceMegaLink[] =
    columnDef?.links
      .filter(
        (link) =>
          isRouteAvailableInLocale({kind: "service-subpage", serviceTag: item.tag, subKey: link.key}, locale) &&
          (showUnpublished || isServiceSubpagePublished(item.tag, link.key)),
      )
      .map((link) => ({
        key: link.key,
        icon: link.icon,
        iconBrand: resolveIconBrand(link.iconBrand, lang),
        label: t(link.title, lang),
        description: t(link.description, lang),
        href: buildServiceSubpageHref(locale, item.tag, link.key),
      })) ?? [];

  return {
    tag: item.tag,
    header: item.label,
    headerHref: item.href,
    viewLabel: t(megaData.viewService, lang).replace("{name}", item.label),
    links,
  };
}
